import 'regenerator-runtime/runtime';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  DoRequestResponse, getEndpointUrl,
  GlobalActions,
  GlobalPut,
  HTTP_METHOD,
  ModalType,
  ResponseType,
} from '@ps20-frontend/shop-integration-redux';
import { AxiosError } from 'axios';
import { IAppState } from '@ps20-frontend/apps/dashboard-store';
import { DashboardAction, DashboardActions } from '../dashboard.actions';
import { downloadBlob } from '../../app/utils/file-download.util';
import { environment } from '../../environments/environment';

export const FILE_DOWNLOAD_URL = '/privatschutz/api/sds/';

export enum FILE_DOWNLOAD_EVENTS {
  FILE_DOWNLOAD_SUCCESSFUL_EVENT = 'FILE_DOWNLOAD_SUCCESSFUL_EVENT',
  FILE_DOWNLOAD_FAILED_EVENT = 'FILE_DOWNLOAD_FAILED_EVENT',
}

export function* watchFileDownload() {
  yield takeEvery(
    DashboardActions.DOWNLOAD_ELEMENTARFRAGEBOGEN_PDF,
    safeSaga(fileDownloadSaga, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    FILE_DOWNLOAD_EVENTS.FILE_DOWNLOAD_SUCCESSFUL_EVENT,
    safeSaga(fileDownloadSuccessCallback, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    FILE_DOWNLOAD_EVENTS.FILE_DOWNLOAD_FAILED_EVENT,
    safeSaga(fileDownloadErrorCallback, App.SHOP_DASHBOARD),
  );
}

export function* fileDownloadSaga(action: DashboardAction<{ uploadId: string; fileName: string }>) {
  const globalPut = GlobalPut.getInstance();
  const { uploadId, fileName } = action.payload;
  const state: IAppState = yield select();
  const apigeeActiveAndNotPerma =
    environment.apigeeActive && !state.transientState.permasession;

  yield put({
    type: DashboardActions.FILE_MODIFICATION_IN_PROGRESS,
  });

  yield call(
    globalPut.put,
    new GlobalActions().doRequest({
      appId: App.SHOP_DASHBOARD,
      url: getEndpointUrl(apigeeActiveAndNotPerma, FILE_DOWNLOAD_URL + uploadId),
      method: HTTP_METHOD.GET,
      successEvent: FILE_DOWNLOAD_EVENTS.FILE_DOWNLOAD_SUCCESSFUL_EVENT,
      errorEvent: FILE_DOWNLOAD_EVENTS.FILE_DOWNLOAD_FAILED_EVENT,
      responseType: ResponseType.ARRAY_BUFFER,
      transformResponse: [
        (data, headers) => {
          return { data, headers };
        },
      ],
      additionalData: {
        fileName,
      },
      retry: {
        times: 3,
        delay: 100,
      },
    }),
  );
}

export function* fileDownloadSuccessCallback(
  successAction: DoRequestResponse<{ data: unknown; headers: unknown }>,
) {
  const additionalData = successAction.doRequestAction.payload.additionalData as {
    fileName: string;
  } | undefined;
  const fileName = additionalData?.fileName || 'download.pdf';
  const responseData = successAction.payload.data as BlobPart;

  try {
    const blob = new Blob([responseData], { type: 'application/pdf' });
    downloadBlob(blob, fileName);
    yield put(new DashboardActions().removeFileDownloadErrorByFileName(fileName));
  } catch {
    yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().requestModal(ModalType.FILE_DOWNLOAD_ERROR, {
        body: 'Fehler beim Verarbeiten der Datei. Bitte versuchen Sie es später erneut.',
        button: 'Schließen',
      }),
    );
  } finally {
    yield put({
      type: DashboardActions.FILE_MODIFICATION_FINISHED,
    });
  }
}

export function* fileDownloadErrorCallback(errorAction: DoRequestResponse<AxiosError>) {
    const additionalData = errorAction.doRequestAction.payload.additionalData as {
    fileName: string;
  };
  const fileName = additionalData?.fileName;

  const errorId = `download-error-${Date.now()}`;

  yield put(new DashboardActions().addFileDownloadError(errorId, fileName, 'download'));

  yield put({
    type: DashboardActions.FILE_MODIFICATION_FINISHED,
  });
}
